import React, { useState, useEffect } from 'react';
import { accessoryService } from '../services/accessoryService';
import './Accessories.css';

function Accessories() {
  const [showForm, setShowForm] = useState(false);
  const [accessoryItems, setAccessoryItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [newAccessory, setNewAccessory] = useState({
    name: '',
    category: '',
    brand: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    price: '',
    status: 'In Use'
  });

  const categories = [
    'Watches',
    'Jewelry',
    'Bags',
    'Belts',
    'Sunglasses',
    'Wallets',
    'Other'
  ];

  useEffect(() => {
    fetchAccessories();
  }, []);

  const fetchAccessories = async () => {
    try {
      const response = await accessoryService.getAccessories();
      setAccessoryItems(response.data);
    } catch (error) {
      console.error('Error fetching accessories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await accessoryService.createAccessory(newAccessory);
      fetchAccessories();
      setNewAccessory({ name: '', category: '', brand: '', purchaseDate: '', price: '', status: 'In Use' });
      setShowForm(false);
    } catch (error) {
      console.error('Error adding accessory:', error);
    }
  };

  const handleEdit = async (item) => {
    if (editingItem?._id === item._id) {
      // Save the edit
      try {
        await accessoryService.updateAccessory(item._id, editingItem);
        setEditingItem(null);
        fetchAccessories();
      } catch (error) {
        console.error('Error updating accessory:', error);
      }
    } else {
      // Start editing
      setEditingItem(item);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await accessoryService.deleteAccessory(id);
        fetchAccessories();
      } catch (error) {
        console.error('Error deleting accessory:', error);
      }
    }
  };

  return (
    <div className="accessories-container">
      <div className="header-actions">
        <h1>Accessories Management</h1>
        <button onClick={() => setShowForm(!showForm)} className="add-button">
          {showForm ? 'Cancel' : 'Add New Accessory'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="add-form">
          <input
            type="text"
            placeholder="Name"
            value={newAccessory.name}
            onChange={(e) => setNewAccessory({...newAccessory, name: e.target.value})}
            required
          />
          <select
            value={newAccessory.category}
            onChange={(e) => setNewAccessory({...newAccessory, category: e.target.value})}
            required
          >
            <option value="">Select Category</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Brand"
            value={newAccessory.brand}
            onChange={(e) => setNewAccessory({...newAccessory, brand: e.target.value})}
            required
          />
          <input
            type="date"
            value={newAccessory.purchaseDate}
            onChange={(e) => setNewAccessory({...newAccessory, purchaseDate: e.target.value})}
            required
          />
          <input
            type="number"
            placeholder="Price"
            value={newAccessory.price}
            onChange={(e) => setNewAccessory({...newAccessory, price: e.target.value})}
            required
          />
          <select
            value={newAccessory.status}
            onChange={(e) => setNewAccessory({...newAccessory, status: e.target.value})}
            required
          >
            <option value="In Use">In Use</option>
            <option value="Stored">Stored</option>
            <option value="Maintenance">Maintenance</option>
            <option value="Lost">Lost</option>
          </select>
          <button type="submit">Add Accessory</button>
        </form>
      )}

      <div className="accessories-grid">
        {accessoryItems.map(item => (
          <div key={item._id} className="accessory-card">
            {editingItem?._id === item._id ? (
              // Edit form
              <div className="edit-form">
                <input
                  type="text"
                  value={editingItem.name}
                  onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                />
                <select
                  value={editingItem.category}
                  onChange={(e) => setEditingItem({...editingItem, category: e.target.value})}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={editingItem.brand}
                  onChange={(e) => setEditingItem({...editingItem, brand: e.target.value})}
                />
                <input
                  type="date"
                  value={editingItem.purchaseDate}
                  onChange={(e) => setEditingItem({...editingItem, purchaseDate: e.target.value})}
                />
                <input
                  type="number"
                  value={editingItem.price}
                  onChange={(e) => setEditingItem({...editingItem, price: e.target.value})}
                />
                <select
                  value={editingItem.status}
                  onChange={(e) => setEditingItem({...editingItem, status: e.target.value})}
                >
                  <option value="In Use">In Use</option>
                  <option value="Stored">Stored</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Lost">Lost</option>
                </select>
              </div>
            ) : (
              // Display view
              <>
                <h3>{item.name}</h3>
                <div className="accessory-details">
                  <p><strong>Category:</strong> {item.category}</p>
                  <p><strong>Brand:</strong> {item.brand}</p>
                  <p><strong>Purchased:</strong> {new Date(item.purchaseDate).toLocaleDateString()}</p>
                  <p><strong>Price:</strong> ${Number(item.price).toFixed(2)}</p>
                  <p className={`status ${(item.status || 'In Use').toLowerCase().replace(/\s+/g, '-')}`}>
                    <strong>Status:</strong> {item.status || 'In Use'}
                  </p>
                </div>
              </>
            )}
            <div className="action-buttons">
              <button 
                onClick={() => handleEdit(item)} 
                className={editingItem?._id === item._id ? "save-button" : "edit-button"}
              >
                {editingItem?._id === item._id ? "Save" : "Edit"}
              </button>
              {editingItem?._id === item._id && (
                <button onClick={() => setEditingItem(null)} className="cancel-button">
                  Cancel
                </button>
              )}
              <button onClick={() => handleDelete(item._id)} className="delete-button">
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Accessories;
